import { PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { useEffect, useRef, useState } from "react";
import {
  CareerTrackRoleType,
  SaveOrgWideCareerTrackMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Avatars from "@components/avatar/avatars";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import useConfirm from "@helpers/hooks/use-confirm";

import AddButton from "../add-button";
import { describeRoleHolders, getDefaultRoleTitle } from "../helpers";
import { CareerTrackFormRole } from "./career-track-form";
import RoleDetailModal from "./role-detail-modal";

// Role name that reads as plain text but can be edited in place. Enter or
// blur applies the change to the form; Escape reverts.
const RoleTitleInput = ({
  title,
  onCommit,
  onBlurSave,
}: {
  title: string;
  onCommit: (title: string) => void;
  onBlurSave?: () => void;
}) => {
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    setDraft(title);
  }, [title]);

  return (
    <input
      ref={inputRef}
      aria-label="Career track: Role name"
      value={draft}
      onChange={(evt) => setDraft(evt.target.value)}
      onKeyDown={(evt) => {
        if (evt.key === "Enter") {
          inputRef.current?.blur();
        } else if (evt.key === "Escape") {
          cancelledRef.current = true;
          inputRef.current?.blur();
        }
      }}
      onBlur={() => {
        if (cancelledRef.current) {
          cancelledRef.current = false;
          setDraft(title);
          return;
        }
        onCommit(draft);
        if (!draft.trim()) {
          setDraft(title);
        }
        // Saved once the commit has rendered: a save already due would
        // otherwise run with the old name, and put it back on screen.
        setTimeout(() => onBlurSave?.());
      }}
      className="w-full min-w-0 -mx-1 px-1 py-0 font-bold text-sm text-gray-900 bg-transparent border-0 rounded-sm truncate hover:bg-gray-50 focus:bg-white focus:outline-hidden focus:ring-1 focus:ring-gray-300"
    />
  );
};

const RoleHeading = ({
  organizationId,
  level,
  isOnlyRole,
  role,
  onRoleChange,
  onNewRole,
  onRemoveRole,
  associatedTeam,
  careerTrackData,
  onBlurSave,
  readOnly,
}: {
  organizationId: number;
  associatedTeam: UserComboboxOption | null;
  careerTrackData: Omit<SaveOrgWideCareerTrackMutationVariables, "roles">;
  level: number;
  // The track's last role is emptied rather than removed, so the table
  // always has a column.
  isOnlyRole?: boolean;
  role?: CareerTrackFormRole;
  onRoleChange: (role: CareerTrackFormRole) => void;
  onNewRole: () => void;
  onRemoveRole: () => void;
  // Autosaving pages save when the role name loses focus.
  onBlurSave?: () => void;
  // View mode: the role's name and who holds it, with nothing to change.
  readOnly?: boolean;
}) => {
  const label = useLabel();
  const [isShowingEditRoleModal, setShowingEditRoleModal] = useState(false);
  const roleTitle = role?.title?.trim()
    ? role.title
    : getDefaultRoleTitle(careerTrackData.title, level);
  const { ConfirmationDialog, confirm, setMessage } = useConfirm(
    "Delete role?",
    "",
  );

  // Who loses the role, and the reviews that go with it (a role not saved yet
  // has none), before where the level ends up.
  const lossMessage = `${describeRoleHolders(role?.assignToUsers ?? [])}${
    role?.id
      ? ` ${label("review", { capitalize: true })} questions and answers about this role are deleted too.`
      : ""
  }`;
  const removeRoleMessage = isOnlyRole
    ? `Delete "${roleTitle}", with its criteria and responsibilities? ${lossMessage} The track is left with one empty level.`
    : `Delete "${roleTitle}", with its role-specific criteria and responsibilities? ${lossMessage} Level ${level} stays in the ladder, and levels above it keep their numbers.`;

  // The confirm dialog is awaited, and a save can land meanwhile: remove
  // with the handler from then, not now.
  const onRemoveRoleRef = useRef(onRemoveRole);
  onRemoveRoleRef.current = onRemoveRole;
  const handleRemoveRole = async () => {
    setMessage(removeRoleMessage);
    if (await confirm()) {
      onRemoveRoleRef.current();
    }
  };

  return (
    <>
      <ConfirmationDialog />
      {isShowingEditRoleModal && role && (
        <RoleDetailModal
          organizationId={organizationId}
          role={role}
          onClose={() => {
            setShowingEditRoleModal(false);
          }}
          onRoleChange={onRoleChange}
          onDelete={onRemoveRole}
          deleteConfirmation={{
            title: "Delete role?",
            message: removeRoleMessage,
          }}
          level={level}
          associatedTeam={associatedTeam}
          careerTrackData={careerTrackData}
        />
      )}

      {/* As tall as its buttons even when there are none (Previewing), so the
          label sits at the same height in both modes, level with the first
          column's header. */}
      <div className="flex items-center justify-between min-h-[24px]">
        {/* Styled like the Overview tables' column headers. */}
        <div className="text-xs font-medium text-gray-500">Level {level}</div>
        {role && !readOnly && (
          <div className="flex items-center gap-2 opacity-0 transition-opacity group-hover/level:opacity-100 focus-within:opacity-100">
            <Button
              theme={buttonTheme.iconGray}
              icon={PencilIcon}
              size={ButtonSize.medium}
              onClick={() => setShowingEditRoleModal(true)}
              tooltip="Edit role"
              aria-label="Career track: Edit role"
            />
            <Button
              theme={buttonTheme.iconGray}
              icon={TrashIcon}
              size={ButtonSize.medium}
              onClick={handleRemoveRole}
              tooltip="Delete role"
              aria-label="Career track: Delete role"
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!role && !readOnly && (
          // The level is in the ladder (something has criteria on it) but this
          // track hasn't said who sits there yet. It takes the role name's
          // line, under "Level N".
          <AddButton
            icon="userPlus01"
            size={ButtonSize.medium}
            text="Add role for this track"
            leftNegativeMargin
            aria-label="Career track: Add role"
            onClick={onNewRole}
          />
        )}
        {role && readOnly && (
          <div className="font-bold text-sm text-gray-900 truncate">
            {roleTitle}
          </div>
        )}
        {role && !readOnly && (
          <RoleTitleInput
            title={roleTitle}
            onBlurSave={onBlurSave}
            onCommit={(title) => {
              // Clearing the name (or leaving the default) keeps it on the
              // default name, which follows the track name.
              const nextTitle =
                title.trim() === "" ||
                (!role.title?.trim() && title.trim() === roleTitle)
                  ? ""
                  : title.trim();
              if (nextTitle !== (role.title ?? "")) {
                onRoleChange({ ...role, title: nextTitle });
              }
            }}
          />
        )}
        {role?.roleType === CareerTrackRoleType.Management && (
          <Tooltip text="Management role">
            <span>
              <TopicflowIcon type="users" className="mt-0.5 h-4 w-4" />
            </span>
          </Tooltip>
        )}
      </div>
      {role && role.assignToUsers.length > 0 ? (
        <div className="mt-2 flex items-center gap-1">
          <button
            type="button"
            className="flex"
            aria-label="Career track: Role members"
            disabled={readOnly}
            onClick={() => setShowingEditRoleModal(true)}
          >
            <Avatars
              max={10}
              users={role.assignToUsers}
              size={5}
              // Avatars overlap by shifting each one left, including the
              // first, so pad it back to line up with the role name above.
              className="pl-1.5"
              clickingWillShowModal={false}
            />
          </button>
          {/* The faces open the same dialog, but nothing says so: this is
              the way in that reads as "add someone". */}
          {!readOnly && (
            <Button
              theme={buttonTheme.iconGray}
              icon={TopicflowIconMap.plus}
              size={ButtonSize.medium}
              onClick={() => setShowingEditRoleModal(true)}
              tooltip="Add members"
              aria-label="Career track: Add role members"
            />
          )}
        </div>
      ) : role && !readOnly ? (
        <div className="mt-2 flex">
          <AddButton
            text="Add members"
            size={ButtonSize.medium}
            leftNegativeMargin
            aria-label="Career track: Add role members"
            onClick={() => setShowingEditRoleModal(true)}
          />
        </div>
      ) : null}
    </>
  );
};

export default RoleHeading;
